import { useEffect, useRef, useState } from "react";
import { BaseInputField, IconArrowDropDown, IconArrowDropUp, Input, Menu, MenuListItem } from "cdk-radial";
import styled from "styled-components";

const SelectInput = styled(Input)`
  ${BaseInputField} {
    cursor: pointer;
  }
  svg {
    cursor: pointer;
  }
`;

/**
 * @typedef {{
 *   label: string,
 *   value: any,
 *   disabled?: boolean
 * }} DropdownOption
 */

/**
 * A ready-to-use, controlled, composed dropdown menu.
 *
 * @param props {Object}
 * @param props.options {DropdownOption[]} An array of options to display in the dropdown menu.
 * @param props.onChange {(option: DropdownOption) => void} A callback to invoke when the user selects an option.
 * @param props.value {DropdownOption} The currently selected option. The parent component is responsible for managing the selection state.
 * @param props.isDisabled {boolean?} If `true`, the dropdown is not interactable and uses disabled styles.
 * @param props.width {string?} The width of the dropdown. 224px by default.
 * @param props.dataTestId {string?} A string to use as the `data-testid` attribute for the input box, and a prefix for the test ID of the menu and menu items.
 * Menu: `{dataTestId}-menu`
 * Menu item: `{dataTestId}-menu-item-{index}`
 * @param props.inputId {string} The ID to apply to the input box.
 * @param props.name {string} The name to apply to the input box.
 * @param props.label {string} The label for this dropdown. This appears in the input box.
 * @param props.placeholder {string?} A string to display in the input box when nothing is selected. "Select" by default.
 * @param props.enableCustomValidation {boolean?} If `true`, setting `hasError` will display an error on the input box.
 * @param props.hasError {boolean?} If `true` and `enableCustomValidation` is also `true`, the input box will display an error state.
 * @param props.errorMessage {string?} The error message displayed when `hasError` is set.
 */
export default function DropdownMenu({
  options,
  onChange,
  value: selectedOption,
  isDisabled,
  width = "224px",
  dataTestId,
  inputId,
  name,
  label,
  placeholder = "Select",
  enableCustomValidation,
  hasError,
  errorMessage
}) {
  const targetRef = useRef();
  const dimensionRef = useRef();
  const menuContainerRef = useRef();

  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSelect = option => {
    onChange(option);
    setIsOpen(false);
  };

  useEffect(() => {
    const setIsOpenFromMenu = event => {
      if (event.detail.ref === menuContainerRef) setIsOpen(event.detail.openMenu);
    };
    document.addEventListener("customMenuOpenEvent", setIsOpenFromMenu);
    return () => document.removeEventListener("customMenuOpenEvent", setIsOpenFromMenu);
  }, []);

  return (
    <>
      <div style={{ width }}>
        <SelectInput
          dataTestId={dataTestId}
          icon={isOpen ? <IconArrowDropUp /> : <IconArrowDropDown />}
          id={inputId}
          label={label}
          labelRef={dimensionRef}
          name={name}
          onChange={() => {}}
          placeholder={placeholder}
          ref={targetRef}
          value={selectedOption?.label ?? ""}
          style={{ caretColor: "transparent" }}
          enableCustomValidation={enableCustomValidation}
          hasError={hasError}
          errorMessage={errorMessage}
          isDisabled={isDisabled}
        />
      </div>
      <Menu
        dataTestId={`${dataTestId}-menu`}
        dimensionRef={dimensionRef}
        isOpen={isOpen}
        labelRef={targetRef}
        onClose={handleClose}
        onOpen={handleOpen}
        style={{ maxWidth: "fit-content" }}
        isAuto={false}
        ref={menuContainerRef}
      >
        {options.map((option, ind) => (
          <MenuListItem
            dataTestId={`${dataTestId}-menu-item-${ind}`}
            isDisabled={option.disabled}
            isSelected={option.value === selectedOption?.value}
            key={ind}
            onClick={() => handleSelect(option)}
            hideSelectedItemCheckmark={false}
          >
            {option.label}
          </MenuListItem>
        ))}
      </Menu>
    </>
  );
}
